// Phase 3 scaffold — single-panorama material swap via mask overlay.
//
// Wallet-friendly material variants: instead of authoring N panoramas per room
// (one per finish), we ship ONE base panorama plus an equirectangular mask
// (black/white PNG aligned to the panorama's UV) per swappable surface, plus
// a small RGB colour for each available finish. At runtime, MaterialOverlay
// renders a second inverted sphere right on top of the panorama with the
// mask driving opacity and the finish colour as flat emissive — the result
// looks like the surface was repainted, without regenerating anything.
//
// The shipping demo uses flat RGB only (paints / solid finishes). Tileable
// textures (walnut grain, marble, smoked oak) need a different UV channel
// to break free of the equirectangular mapping — that's the Phase 3 full
// pipeline. The scaffold is enough for the "developer offers 3 paint
// options" sales scenario, which is the most common real-estate use case.
//
// Matches PanoramaSphere/MaskOverlay UV conventions exactly so the mask
// aligns:
// • cull = CULLFACE_FRONT (camera inside)
// • emissiveMapTiling = (-1, 1) + offset (1, 0) for U-flip — and the SAME
//   transform set independently on the opacityMap slot. StandardMaterial
//   keeps a separate UV transform per map slot (_defineTex2D generates
//   `${name}MapTiling`/`${name}MapOffset` for every slot), so setting only
//   the emissive pair samples the mask at 1-u for colour and at u for
//   alpha — a mirrored mask that leaves part of the real surface unpainted
//   and blends black over the mirrored region at full alpha.
// • entity rotated -90° around Y so equirect u=0.5 faces camera's -Z forward
// • radius=101 + depthTest=false: the overlay always paints on top of the
//   panorama regardless of which is drawn first. Without depthTest=false,
//   the panorama at radius=100 writes depth=100 and the overlay at
//   radius=101 fails the standard LESS test (it's farther from the
//   camera-at-origin, so its fragments have higher depth). depthTest=false
//   mirrors the dollhouse FOV-cone pattern documented in CLAUDE.md.
//
// Lifecycle: instantiated lazily by App on the first surface hotspot that
// declares a maskUrl + materials. Persistent for the app's lifetime; hidden
// (entity.enabled=false) when no finish is active.

import * as pc from 'playcanvas';

const RADIUS = 101;
const LON_BANDS = 128;
const LAT_BANDS = 64;

/**
 * Ratio that a fully-white texel of the shading map decodes to. MUST match
 * `--max` in scripts/bake-surface-shading.mjs (its default is 2.5), because
 * the bake stores `(ratio / max) ^ (1/2.2)` and StandardMaterial computes
 *
 *     dEmission = material_emissive * material_emissiveIntensity * pow(map, 2.2)
 *
 * so putting `max` in emissiveIntensity is exactly what recovers the ratio.
 * Change one, change the other, or every finish shifts brightness.
 */
export const SHADING_RATIO_MAX = 2.5;

export interface ApplyOptions {
  maskUrl: string;
  /** RGB the masked region gets painted with (each channel 0–1). */
  color: [number, number, number];
  /**
   * Optional equirect shading-ratio map (see SurfaceHotspot.shadingUrl). When
   * present the finish is multiplied by the panorama's own lighting, so
   * shadows and AO survive the repaint. When absent the surface renders as a
   * flat fill — the original Sprint 1 behaviour.
   */
  shadingUrl?: string;
}

export class MaterialOverlay {
  readonly entity: pc.Entity;
  private readonly app: pc.Application;
  private readonly material: pc.StandardMaterial;
  private maskTexture: pc.Texture | null = null;
  private currentMaskUrl: string | null = null;
  private shadingTexture: pc.Texture | null = null;
  private currentShadingUrl: string | null = null;
  /** Bumped per apply() so a slow texture load can't overwrite a newer pick. */
  private applyGeneration = 0;

  constructor(app: pc.Application) {
    this.app = app;

    const mesh = pc.createSphere(app.graphicsDevice, {
      radius: RADIUS,
      latitudeBands: LAT_BANDS,
      longitudeBands: LON_BANDS,
    });

    this.material = new pc.StandardMaterial();
    this.material.useLighting = false;
    this.material.useFog = false;
    this.material.useTonemap = false;
    this.material.useSkybox = false;
    this.material.diffuse = new pc.Color(0, 0, 0);
    this.material.emissive = new pc.Color(1, 1, 1);
    this.material.cull = pc.CULLFACE_FRONT;
    // Same U-flip the panorama sphere uses, applied to BOTH map slots.
    // StandardMaterial keeps an independent UV transform per slot, so the
    // opacity pair must be set explicitly or the mask is sampled mirrored
    // for alpha. Keep these four lines in sync.
    this.material.emissiveMapTiling = new pc.Vec2(-1, 1);
    this.material.emissiveMapOffset = new pc.Vec2(1, 0);
    this.material.opacityMapTiling = new pc.Vec2(-1, 1);
    this.material.opacityMapOffset = new pc.Vec2(1, 0);
    this.material.opacity = 1.0;
    this.material.blendType = pc.BLEND_NORMAL;
    this.material.depthWrite = false;
    // Always paint on top — the overlay sphere is at radius=101, which is
    // farther from the camera-at-origin than the panorama (depth=100 < 101)
    // so the standard LESS depth test would reject it. Disable the test so
    // the mask repaint composites regardless of geometric ordering.
    this.material.depthTest = false;
    this.material.update();

    const meshInstance = new pc.MeshInstance(mesh, this.material);
    meshInstance.castShadow = false;
    meshInstance.receiveShadow = false;

    this.entity = new pc.Entity('material-overlay');
    this.entity.addComponent('render', { meshInstances: [meshInstance] });
    this.entity.setLocalPosition(0, 0, 0);
    this.entity.setLocalEulerAngles(0, -90, 0);
    this.entity.enabled = false;
    app.root.addChild(this.entity);
  }

  /**
   * Activate a finish on a masked surface. Loads the mask texture once per
   * URL (cached for repeat picks within the same room), sets the emissive
   * to the finish colour, and shows the overlay. Concurrent calls with the
   * same maskUrl just retint without reloading the mask.
   */
  async apply(opts: ApplyOptions): Promise<void> {
    const generation = ++this.applyGeneration;
    const [r, g, b] = opts.color;
    this.material.emissive = new pc.Color(r, g, b);

    try {
      if (opts.maskUrl !== this.currentMaskUrl || !this.maskTexture) {
        const texture = await loadTextureFromUrl(this.app, opts.maskUrl);
        // A newer pick landed while this decode was in flight — drop ours.
        if (generation !== this.applyGeneration) { texture.destroy(); return; }
        this.maskTexture?.destroy();
        this.maskTexture = texture;
        this.currentMaskUrl = opts.maskUrl;
      }

      if (opts.shadingUrl && (opts.shadingUrl !== this.currentShadingUrl || !this.shadingTexture)) {
        const texture = await loadTextureFromUrl(this.app, opts.shadingUrl);
        if (generation !== this.applyGeneration) { texture.destroy(); return; }
        this.shadingTexture?.destroy();
        this.shadingTexture = texture;
        this.currentShadingUrl = opts.shadingUrl;
      }
    } catch (err) {
      console.error('[material-overlay] failed to load textures', opts, err);
      this.clear();
      return;
    }

    // The mask always drives alpha. What drives colour depends on whether a
    // shading map was baked:
    //  • with one: `emissive * SHADING_RATIO_MAX * decode(shading)`, i.e. the
    //    finish colour modulated by the room's real lighting.
    //  • without: `emissive * mask.rgb` — the Sprint 1 flat fill. The mask
    //    doubles as the emissive map because StandardMaterial skips the alpha
    //    pass entirely on some hardware paths when emissiveMap is null;
    //    empirically the dual-map wiring is what makes the overlay visible.
    const useShading = Boolean(opts.shadingUrl) && this.shadingTexture !== null;
    this.material.emissiveMap = useShading ? this.shadingTexture : this.maskTexture;
    this.material.emissiveIntensity = useShading ? SHADING_RATIO_MAX : 1;
    this.material.opacityMap = this.maskTexture;
    this.material.opacityMapChannel = 'r';

    this.material.update();
    this.entity.enabled = true;
    console.info('[material-overlay] applied', {
      color: opts.color,
      maskUrl: opts.maskUrl,
      shading: useShading ? opts.shadingUrl : 'none (flat fill)',
      maskSize: this.maskTexture ? `${this.maskTexture.width}×${this.maskTexture.height}` : 'none',
    });
  }

  /** Hide the overlay (entity disabled). Texture stays cached. */
  clear(): void {
    this.entity.enabled = false;
  }

  /** Release the cached textures. Call when changing rooms so we don't grow GPU memory. */
  release(): void {
    this.entity.enabled = false;
    this.applyGeneration++; // invalidate any decode still in flight
    this.maskTexture?.destroy();
    this.maskTexture = null;
    this.currentMaskUrl = null;
    this.shadingTexture?.destroy();
    this.shadingTexture = null;
    this.currentShadingUrl = null;
    this.material.emissiveMap = null;
    this.material.opacityMap = null;
    this.material.emissiveIntensity = 1;
    this.material.update();
  }

  isVisible(): boolean {
    return this.entity.enabled;
  }

  destroy(): void {
    this.release();
    this.entity.destroy();
    this.material.destroy();
  }
}

async function loadTextureFromUrl(app: pc.Application, url: string): Promise<pc.Texture> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = url;
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`material-overlay: mask image failed to load: ${url}`));
  });
  const texture = new pc.Texture(app.graphicsDevice, {
    width: img.naturalWidth,
    height: img.naturalHeight,
    format: pc.PIXELFORMAT_RGBA8,
    mipmaps: true,
    addressU: pc.ADDRESS_REPEAT,
    addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
    magFilter: pc.FILTER_LINEAR,
  });
  texture.setSource(img);
  return texture;
}
